import { Alert, Button, Descriptions, Drawer, Empty, Spin, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useAttendanceDay } from "../hooks/useAttendance";
import { useIsRecomputing } from "../store/recompute.store";
import type { AttendanceEvent } from "../types";
import { isPreliminary } from "../utils/preliminary";
import { CorrectionModal } from "./CorrectionModal";

const { Text } = Typography;

const EVENT_COLORS: Record<AttendanceEvent["type"], string> = {
	// biome-ignore lint/style/useNamingConvention: backend hodisa turi enum qiymati aynan shu
	check_in: "green",
	// biome-ignore lint/style/useNamingConvention: backend hodisa turi enum qiymati aynan shu
	check_out: "blue",
	departure: "red",
	return: "green",
	correction: "gold",
	rejection: "red",
};

/**
 * Check-in aniqligi belgisi (spec §4.3, kontrakt K4).
 *
 * ⚠️ Qoida SERVER tomonida YOZISH paytida qo'llangan va
 * `payload.lowAccuracy` ga muzlatilgan — bu yerda `gps_accuracy_max_m`
 * bilan QAYTA hisoblanmaydi (chegara o'zgarsa o'tmish qayta talqin
 * qilinmasin). Eski hodisalarda kalit yo'q → belgi chiqmaydi.
 */
function AniqlikTag({ payload }: { payload: AttendanceEvent["payload"] }) {
	const { t } = useTranslation("attendance");

	if (payload?.lowAccuracy !== true) {
		return null;
	}

	const aniqlik = payload.accuracyM;

	return (
		<Tag color="orange">
			{typeof aniqlik === "number"
				? t("detail.lowAccuracyWithMeters", { value: Math.round(aniqlik) })
				: t("detail.lowAccuracy")}
		</Tag>
	);
}

interface DayDetailDrawerProps {
	dayId: string | null;
	onClose: () => void;
}

export function DayDetailDrawer({ dayId, onClose }: DayDetailDrawerProps) {
	const { t } = useTranslation("attendance");
	const { data, isLoading } = useAttendanceDay(dayId);
	const isRecomputing = useIsRecomputing(dayId);
	const [correctionOpen, setCorrectionOpen] = useState(false);

	const eventLabels: Record<AttendanceEvent["type"], string> = {
		// biome-ignore lint/style/useNamingConvention: backend hodisa turi enum qiymati aynan shu
		check_in: t("events.check_in"),
		// biome-ignore lint/style/useNamingConvention: backend hodisa turi enum qiymati aynan shu
		check_out: t("events.check_out"),
		departure: t("events.departure"),
		return: t("events.return"),
		correction: t("events.correction"),
		rejection: t("events.rejection"),
	};

	const sourceLabels: Record<AttendanceEvent["source"], string> = {
		mobile: t("sources.mobile"),
		derived: t("sources.derived"),
		manual: t("sources.manual"),
	};

	return (
		/* ⚠️ REJADAN CHEKINISH: reja `width={560}` yozgan edi — antd 6 da u
		   `@deprecated` va runtime ogohlantirish beradi; to'g'ri prop `size`
		   (`sizeType | number | string`). */
		<Drawer title={t("detail.title")} size={560} open={dayId !== null} onClose={onClose}>
			{isLoading || !data ? (
				<Spin />
			) : (
				<>
					{/* ⚠️ Dastlabki kun ALOHIDA ogohlantirish bilan: bu qiymatlar
					    kech kelgan paket kelganda O'ZGARISHI mumkin.
					    ⚠️ Mezon `isPreliminary()` da — u javobning `violations` ini
					    o'qiydi, mahalliy qayta hisoblamaydi. Ilgari bu yerdagi
					    shart sahifadagi bilan bir xil kalendar taqqoslash edi va
					    ekranda «toliqsiz_malumot» tegi turgan holda ogohlantirish
					    BO'LMASLIGI mumkin edi — bitta payload'dan ikki qarama-qarshi
					    signal.
					    ⚠️ REJADAN CHEKINISH: reja `message=` yozgan edi — antd 6 da
					    u `@deprecated`, to'g'ri prop `title=` (loyihaning boshqa
					    13 ta `<Alert>` i ham shunday). */}
					{/* ⚠️ Tuzatish YOZILDI, lekin proyeksiya hali qayta qurilmagan:
					    quyidagi `Holat` / `Kechikish` / `Hududda` qiymatlari hamon
					    ESKI. Ogohlantirishsiz rahbar tuzatish ishlamadi deb xulosa
					    qilib uni QAYTA yuborardi va APPEND-ONLY jurnalga takroriy
					    hodisa tushardi (`recompute.store.ts` izohi). */}
					{isRecomputing ? (
						<Alert
							className="mb-4"
							type="info"
							showIcon
							title={t("detail.recomputingTitle")}
							description={t("detail.recomputingDescription")}
						/>
					) : null}

					{isPreliminary(data.day) ? (
						<Alert
							className="mb-4"
							type="warning"
							showIcon
							title={t("detail.preliminaryTitle")}
							description={t("detail.preliminaryDescription")}
						/>
					) : null}

					{/* ⚠️ REJADAN CHEKINISH: reja `<Descriptions.Item>` bolalarini
					    ishlatgan edi — antd 6 da `children` `@deprecated`, to'g'ri
					    yo'l `items` proplari. */}
					<Descriptions
						column={2}
						size="small"
						className="mb-4"
						items={[
							{ key: "workDate", label: t("detail.fields.workDate"), children: data.day.workDate },
							{ key: "status", label: t("detail.fields.status"), children: data.day.status },
							{
								key: "late",
								label: t("detail.fields.late"),
								children: t("detail.minutesValue", { value: data.day.lateMinutes }),
							},
							{
								key: "early",
								label: t("detail.fields.early"),
								children: t("detail.minutesValue", { value: data.day.earlyMinutes }),
							},
							{
								key: "inZone",
								label: t("detail.fields.inZone"),
								children: t("detail.minutesValue", { value: data.day.timeInZoneMin }),
							},
							{
								key: "outside",
								label: t("detail.fields.outside"),
								children: t("detail.minutesValue", { value: data.day.timeOutsideMin }),
							},
							{
								key: "computedAt",
								label: t("detail.fields.computedAt"),
								children: dayjs(data.day.computedAt).format("DD.MM.YYYY HH:mm"),
							},
							{
								key: "watermark",
								label: t("detail.fields.watermark"),
								children: data.day.computedFromWatermark
									? dayjs(data.day.computedFromWatermark).format("DD.MM.YYYY HH:mm")
									: "—",
							},
						]}
					/>

					{data.day.violations.length > 0 ? (
						<div className="mb-4 flex flex-wrap gap-1">
							{data.day.violations.map((violation) => (
								<Tag color="red" key={`${violation.kind}-${JSON.stringify(violation.details)}`}>
									{violation.kind}
								</Tag>
							))}
						</div>
					) : null}

					{data.events.length === 0 ? (
						<Empty description={t("detail.noEvents")} />
					) : (
						/* ⚠️ REJADAN CHEKINISH: reja element ichida `children` yozgan
						   edi — antd 6 `TimelineItemType` da u `@deprecated`, to'g'ri
						   maydon `content`. `key` ham qo'shildi. */
						<Timeline
							items={data.events.map((event) => ({
								key: event.id,
								color: EVENT_COLORS[event.type],
								content: (
									<div>
										<Text strong>{dayjs(event.occurredAt).format("HH:mm")}</Text>{" "}
										<Text>{eventLabels[event.type]}</Text> <Tag>{sourceLabels[event.source]}</Tag>
										{/* ⚠️ **Selfi — spec §6.5 tasmaning to'rtinchi elementi.**
										    Rasmning O'ZI hozircha KO'RSATILMAYDI: `GET /files/{id}`
										    metama'lumot qaytaradi, bayt emas, va
										    `attendance_events.photo_file_id` uchun bayt beradigan
										    marshrut na kontrakt §9 da, na B blokida bor
										    (`GET /employees/{id}/photo` faqat profil rasmi uchun).
										    Yangi marshrut o'ylab topish kontrakt qoidasini buzardi —
										    shuning uchun tasma selfining BORLIGINI ko'rsatadi va
										    bo'shliq koordinatorga NUQSON-8 sifatida yetkaziladi. */}
										{event.photoFileId ? <Tag color="purple">{t("detail.selfieTag")}</Tag> : null}
										<AniqlikTag payload={event.payload} />
										{/* Bekor qilingan hodisa jurnalda QOLADI — foydalanuvchi
										    uni ko'rishi kerak, aks holda tuzatish «yo'qolgan
										    ma'lumot» taassurotini berardi. */}
										{event.correctedEventId ? (
											<Tag color="gold">{t("detail.correctionTag")}</Tag>
										) : null}
										{event.reason ? (
											<div>
												<Text type="secondary">{event.reason}</Text>
											</div>
										) : null}
									</div>
								),
							}))}
						/>
					)}

					<PermissionGate resource="attendance" action="correct">
						<Button type="primary" className="mt-4" onClick={() => setCorrectionOpen(true)}>
							{t("detail.correctButton")}
						</Button>
					</PermissionGate>

					<CorrectionModal
						dayId={dayId}
						events={data.events}
						open={correctionOpen}
						onClose={() => setCorrectionOpen(false)}
					/>
				</>
			)}
		</Drawer>
	);
}
